import React from 'react';
import { X, FileXls, Check, Warning, ArrowRight } from '@phosphor-icons/react';
import { backdropClick, stopBackdropPropagation } from '../../lib/modalBackdrop';

export default function LeadImportPreviewModal({ importPreview, importing, onClose, onConfirm }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" {...backdropClick(onClose, importing)}>
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[85vh] overflow-y-auto p-6" {...stopBackdropPropagation} data-testid="import-preview-modal">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2">
            <FileXls size={24} className="text-emerald-600" /> Anteprima Import
          </h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X size={20} /></button>
        </div>

        <div className="grid grid-cols-3 gap-3 mb-4">
          <div className="bg-slate-50 rounded-lg p-3 text-center">
            <div className="text-2xl font-bold text-slate-900">{importPreview.total_rows}</div>
            <div className="text-xs text-slate-500">Righe nel file</div>
          </div>
          <div className="bg-blue-50 rounded-lg p-3 text-center">
            <div className="text-2xl font-bold text-blue-700">{importPreview.updates_count}</div>
            <div className="text-xs text-blue-600">Leads da aggiornare</div>
          </div>
          <div className="bg-emerald-50 rounded-lg p-3 text-center">
            <div className="text-2xl font-bold text-emerald-700">{importPreview.new_count}</div>
            <div className="text-xs text-emerald-600">Nuovi leads</div>
          </div>
        </div>

        <div className="mb-4">
          <h3 className="text-sm font-semibold text-slate-700 mb-2">Auto-mapping colonne ({importPreview.mapped_count}/{importPreview.columns.length})</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-1.5 max-h-40 overflow-y-auto">
            {importPreview.columns.map(col => (
              <div key={col} className={`flex items-center gap-1.5 text-xs px-2 py-1.5 rounded ${importPreview.mapping[col] ? 'bg-emerald-50 text-emerald-800' : 'bg-amber-50 text-amber-800'}`}>
                {importPreview.mapping[col] ? <Check size={12} weight="bold" /> : <Warning size={12} />}
                <span className="font-medium truncate">{col}</span>
                {importPreview.mapping[col] && (
                  <><ArrowRight size={10} className="text-slate-400 shrink-0" /><span className="text-slate-600 truncate">{importPreview.mapping[col]}</span></>
                )}
              </div>
            ))}
          </div>
          {importPreview.unmapped_columns.length > 0 && (
            <p className="text-xs text-amber-600 mt-1">Colonne ignorate: {importPreview.unmapped_columns.join(', ')}</p>
          )}
        </div>

        {importPreview.updates.length > 0 && (
          <div className="mb-4">
            <h3 className="text-sm font-semibold text-slate-700 mb-2">
              Modifiche ({importPreview.total_field_changes} campi su {importPreview.updates_count} leads)
            </h3>
            <div className="max-h-48 overflow-y-auto border rounded-lg">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 sticky top-0">
                  <tr>
                    <th className="text-left p-2 font-medium">Lead</th>
                    <th className="text-left p-2 font-medium">Campo</th>
                    <th className="text-left p-2 font-medium">Valore Attuale</th>
                    <th className="text-left p-2 font-medium">Nuovo Valore</th>
                  </tr>
                </thead>
                <tbody>
                  {importPreview.updates.slice(0, 50).map((upd, i) =>
                    upd.modified_fields.map((f, j) => (
                      <tr key={`${upd.ragione_sociale}-${f.field}-${i}`} className="border-t hover:bg-slate-50">
                        <td className="p-2 font-medium text-slate-700">{j === 0 ? upd.ragione_sociale : ''}</td>
                        <td className="p-2 text-slate-600">{f.field}</td>
                        <td className="p-2 text-red-500 line-through">{f.old_value || <span className="text-slate-300 no-underline">(vuoto)</span>}</td>
                        <td className="p-2 text-emerald-700 font-medium">{f.new_value}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
              {importPreview.updates.length > 50 && (
                <p className="text-xs text-slate-500 p-2 text-center">...e altri {importPreview.updates.length - 50} leads</p>
              )}
            </div>
          </div>
        )}

        <div className="flex justify-end gap-2 mt-4 pt-4 border-t">
          <button onClick={onClose}
            className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-md">Annulla</button>
          <button onClick={onConfirm} disabled={importing}
            className="px-6 py-2 text-sm bg-orange-500 hover:bg-orange-600 text-white rounded-md font-medium disabled:opacity-50"
            data-testid="confirm-import-btn">
            {importing ? 'Importazione...' : `Conferma Import (${importPreview.updates_count} agg. + ${importPreview.new_count} nuovi)`}
          </button>
        </div>
      </div>
    </div>
  );
}
